import { onBeforeUnmount, watch, type Ref } from 'vue'
import { useEventListener } from '@vueuse/core'
import { clipRect, rectFrom, rectsHit, toggleId, type Rect } from '@/utils/list-selection'

/**
 * Pointer gestures for list selection:
 * - Shift+drag: toggle every item the pointer passes over, once per drag.
 *   Never starts native drag-and-drop or Sortable reordering.
 * - Plain drag from empty space: marquee; selection becomes the touched items.
 * - Plain drag on an item: left alone (drag-to-folder, reorder, ...).
 * Below the movement threshold nothing happens, so clicks keep their meaning.
 */

export const DRAG_THRESHOLD = 4

/** Interactive descendants that never start a gesture. */
export const INTERACTIVE_SELECTOR = [
  'button',
  'a',
  'input',
  'textarea',
  'select',
  '[role="button"]',
  '[role="menuitem"]',
  '[role="checkbox"]',
  '.v-menu-activator',
  '.ant-checkbox-wrapper',
  '.ag-header',
  '.ag-paging-panel',
].join(',')

/** The click landed on a control inside the item (menu button, checkbox, ...). */
export const isInteractiveClick = (e: Event, selector = INTERACTIVE_SELECTOR): boolean => {
  const hit = (e.target as Element | null)?.closest?.(selector)
  return !!hit && hit !== e.currentTarget && !!(e.currentTarget as Element | null)?.contains(hit)
}

export interface DragSelectOptions {
  container: Ref<HTMLElement | null | undefined>
  itemSelector: string
  idOf?: (el: HTMLElement) => string | null | undefined
  /** Marquee may only start inside this element (default: anywhere in container). */
  startAreaSelector?: string
  /** Element whose visible rect clips hit-testing and whose scroll is tracked. */
  viewport?: () => HTMLElement | null | undefined
  excludeSelector?: string
  getSelected: () => string[]
  setSelected: (ids: string[]) => void
  enabled?: () => boolean
}

let active: { abort: () => void } | null = null

/** Abort the gesture in progress (restoring the selection). True if there was one. */
export const abortActiveDrag = (): boolean => {
  if (!active) return false
  active.abort()
  return true
}

export function useDragSelect(opts: DragSelectOptions) {
  const idOf = opts.idOf ?? ((el: HTMLElement) => el.dataset.selectId)
  const exclude = opts.excludeSelector ?? INTERACTIVE_SELECTOR

  let pending: {
    pointerId: number
    mode: 'paint' | 'marquee'
    startX: number
    startY: number
    startItem: HTMLElement | null
  } | null = null
  let gesture: {
    mode: 'paint' | 'marquee'
    snapshot: string[]
    visited: Set<string>
    startX: number
    startY: number
    startScrollLeft: number
    startScrollTop: number
    lastX: number
    lastY: number
    overlay?: HTMLDivElement
    raf: number
    pointerId: number
  } | null = null

  const scrollEl = () => opts.viewport?.() ?? opts.container.value

  const itemAt = (x: number, y: number) => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>(opts.itemSelector)
    return el && opts.container.value?.contains(el) ? el : null
  }

  const toggle = (el: HTMLElement | null) => {
    const id = el && idOf(el)
    if (!gesture || !id || gesture.visited.has(id)) return
    gesture.visited.add(id)
    opts.setSelected(toggleId(opts.getSelected(), id))
  }

  const marqueeRect = (): Rect | null => {
    const g = gesture
    const view = scrollEl()
    if (!g || !view) return null
    const dx = view.scrollLeft - g.startScrollLeft
    const dy = view.scrollTop - g.startScrollTop
    const r = view.getBoundingClientRect()
    return clipRect(rectFrom(g.startX - dx, g.startY - dy, g.lastX, g.lastY), {
      left: r.left,
      top: r.top,
      right: r.right,
      bottom: r.bottom,
    })
  }

  const updateMarquee = () => {
    if (!gesture || gesture.mode !== 'marquee') return
    gesture.raf = 0
    const rect = marqueeRect()
    const o = gesture.overlay!
    if (!rect) {
      o.style.display = 'none'
      opts.setSelected([])
      return
    }
    Object.assign(o.style, {
      display: 'block',
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.right - rect.left}px`,
      height: `${rect.bottom - rect.top}px`,
    })
    const items = Array.from(
      opts.container.value?.querySelectorAll<HTMLElement>(opts.itemSelector) ?? [],
    ).flatMap((el) => {
      const id = idOf(el)
      return id ? [{ id, rect: el.getBoundingClientRect() }] : []
    })
    opts.setSelected(rectsHit(items, rect))
  }

  const scheduleMarquee = () => {
    if (gesture && gesture.mode === 'marquee' && !gesture.raf)
      gesture.raf = requestAnimationFrame(updateMarquee)
  }

  // ponytail: no edge autoscroll; items scrolled out of view can't be
  // marquee-selected. Add a rAF scroll loop near the edges if users need it.
  const begin = (x: number, y: number) => {
    const p = pending!
    const view = scrollEl()
    gesture = {
      mode: p.mode,
      snapshot: [...opts.getSelected()],
      visited: new Set(),
      startX: p.startX,
      startY: p.startY,
      startScrollLeft: view?.scrollLeft ?? 0,
      startScrollTop: view?.scrollTop ?? 0,
      lastX: x,
      lastY: y,
      raf: 0,
      pointerId: p.pointerId,
    }
    pending = null
    active = { abort }
    try {
      opts.container.value?.setPointerCapture(gesture.pointerId)
    } catch {
      // pointer already released
    }
    if (gesture.mode === 'paint') {
      toggle(p.startItem)
      toggle(itemAt(x, y))
    } else {
      const o = document.createElement('div')
      o.className = 'drag-select-marquee'
      o.style.cssText =
        'position:fixed;pointer-events:none;z-index:10000;display:none;' +
        'border:1px solid var(--sds-color-border-brand-default, #3b82f6);' +
        'background:rgba(59,130,246,0.12);'
      document.body.appendChild(o)
      gesture.overlay = o
      scheduleMarquee()
    }
  }

  const finish = (restore: boolean) => {
    const g = gesture
    pending = null
    if (!g) return
    if (g.raf) cancelAnimationFrame(g.raf)
    // Apply the last pointer position before ending (its frame may be pending).
    if (!restore && g.mode === 'marquee') updateMarquee()
    gesture = null
    if (active?.abort === abort) active = null
    g.overlay?.remove()
    try {
      opts.container.value?.releasePointerCapture(g.pointerId)
    } catch {
      // already released
    }
    if (restore) opts.setSelected(g.snapshot)
    // The click that ends a real drag must not also toggle/open anything.
    const el = opts.container.value
    if (el) {
      const swallow = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()
      }
      el.addEventListener('click', swallow, { capture: true, once: true })
      setTimeout(() => el.removeEventListener('click', swallow, { capture: true }), 0)
    }
  }

  function abort() {
    finish(true)
  }

  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.altKey) return
    if (opts.enabled && !opts.enabled()) return
    const target = e.target as HTMLElement | null
    if (!target || target.closest(exclude)) return
    const item = target.closest<HTMLElement>(opts.itemSelector)
    if (e.shiftKey) {
      // Keep Sortable / native drag from seeing a Shift gesture.
      e.stopPropagation()
      pending = {
        pointerId: e.pointerId,
        mode: 'paint',
        startX: e.clientX,
        startY: e.clientY,
        startItem: item,
      }
      return
    }
    if (item) return
    if (opts.startAreaSelector && !target.closest(opts.startAreaSelector)) return
    pending = {
      pointerId: e.pointerId,
      mode: 'marquee',
      startX: e.clientX,
      startY: e.clientY,
      startItem: null,
    }
  }

  const onMouseDown = (e: MouseEvent) => {
    if (!pending) return
    // Blocks text selection, Shift+click text extension and native drag.
    e.preventDefault()
    if (pending.mode === 'paint') e.stopPropagation()
  }

  const onPointerMove = (e: PointerEvent) => {
    if (pending && e.pointerId === pending.pointerId) {
      if (Math.hypot(e.clientX - pending.startX, e.clientY - pending.startY) < DRAG_THRESHOLD)
        return
      begin(e.clientX, e.clientY)
      return
    }
    if (!gesture || e.pointerId !== gesture.pointerId) return
    gesture.lastX = e.clientX
    gesture.lastY = e.clientY
    if (gesture.mode === 'paint') toggle(itemAt(e.clientX, e.clientY))
    else scheduleMarquee()
  }

  const onPointerUp = (e: PointerEvent) => {
    if (pending && e.pointerId === pending.pointerId) pending = null
    if (gesture && e.pointerId === gesture.pointerId) finish(false)
  }

  const onDragStart = (e: DragEvent) => {
    if (!pending && !gesture) return
    e.preventDefault()
    e.stopPropagation()
  }

  const onSelectStart = (e: Event) => {
    if (gesture) e.preventDefault()
  }

  useEventListener(opts.container, 'pointerdown', onPointerDown, { capture: true })
  useEventListener(opts.container, 'mousedown', onMouseDown, { capture: true })
  useEventListener(opts.container, 'dragstart', onDragStart, { capture: true })
  useEventListener(window, 'pointermove', onPointerMove)
  useEventListener(window, 'pointerup', onPointerUp)
  useEventListener(window, 'pointercancel', () => abort())
  useEventListener(window, 'blur', () => abort())
  useEventListener(document, 'selectstart', onSelectStart)
  useEventListener(opts.container, 'scroll', scheduleMarquee, { capture: true })

  // The list re-rendered away (loading, view switch): abandon the gesture.
  watch(opts.container, (el) => {
    if (!el) abort()
  })
  onBeforeUnmount(() => abort())

  return { abort, isActive: () => !!gesture }
}
